/**
 * Toast notification system.
 *
 * `useToast()` returns `{ toast(msg, opts?) }`. The `<ToastHost />` is mounted
 * once in the root tree. Toasts auto-dismiss after 4s, stack top-right.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'danger' | 'info';

export interface Toast {
    id: string;
    kind: ToastKind;
    message: string;
    detail?: string;
}

interface ToastContext {
    toasts: Toast[];
    toast: (message: string, opts?: { kind?: ToastKind; detail?: string }) => void;
    dismiss: (id: string) => void;
}

const Ctx = createContext<ToastContext | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const toast = useCallback<ToastContext['toast']>(
        (message, opts) => {
            const id = `t${++idRef.current}`;
            setToasts((prev) => [
                ...prev,
                { id, kind: opts?.kind ?? 'success', message, detail: opts?.detail }
            ]);
            setTimeout(() => dismiss(id), 4000);
        },
        [dismiss]
    );

    const value = useMemo(() => ({ toasts, toast, dismiss }), [toasts, toast, dismiss]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useToast() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useToast must be used inside ToastProvider');
    return ctx;
}

export function ToastHost() {
    const ctx = useContext(Ctx);
    if (!ctx) return null;
    return (
        <div className="toast-host" aria-live="polite" aria-atomic="true">
            {ctx.toasts.map((t) => (
                <ToastRow key={t.id} toast={t} onDismiss={() => ctx.dismiss(t.id)} />
            ))}
        </div>
    );
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
    useEffect(() => {
        // Small entrance animation handled via CSS class
    }, []);
    const Icon =
        toast.kind === 'success' ? CheckCircle : toast.kind === 'danger' ? AlertCircle : Info;
    return (
        <div className="toast" data-kind={toast.kind}>
            <Icon size={14} />
            <div className="toast-body">
                <div className="toast-message">{toast.message}</div>
                {toast.detail && <div className="toast-detail">{toast.detail}</div>}
            </div>
            <button className="toast-close" onClick={onDismiss} aria-label="Dismiss">
                <X size={12} />
            </button>
        </div>
    );
}
